import { useCallback } from 'react';

import type { Move } from '@trello/action-history';
import { ActionHistory } from '@trello/action-history';
import { isMemberLoggedIn, useMemberId } from '@trello/authentication';
import { isMemberOfBoard } from '@trello/business-logic/board';
import { client } from '@trello/graphql';
import { useBoardId, useListId } from '@trello/id-context';

import { MemberState } from 'app/scripts/view-models/MemberState';
import { readListVisibleCardsFromCache } from 'app/src/components/List/readListVisibleCardsFromCache';
import type { MoveCardCurrentListFragment } from './MoveCardCurrentListFragment.generated';
import { MoveCardCurrentListFragmentDoc } from './MoveCardCurrentListFragment.generated';
import type { SuggestedMovesCurrentBoardFragment } from './SuggestedMovesCurrentBoardFragment.generated';
import { SuggestedMovesCurrentBoardFragmentDoc } from './SuggestedMovesCurrentBoardFragment.generated';

export interface SuggestedList {
  id: string;
  direction: 'left' | 'right';
  name: string;
  targetIndex: number;
}

/**
 * This hook returns move suggestions for the MoveCardPopover.
 * Suggestions are restricted to open lists on the current board
 * that have been moved to from the current list.
 * The 5 most recent valid list moves are returned as suggestions after
 * duplicate lists are removed.
 */
export const useSuggestedMoves = () => {
  const currentBoardId = useBoardId();
  const currentListId = useListId();
  const memberId = useMemberId();
  const isLoggedIn = isMemberLoggedIn();
  const MEMBER_STATE = 'showSuggestions';
  const MAX_SUGGESTIONS = 5;

  const getSuggestedMoves = useCallback(() => {
    const currentListData = client.readFragment<MoveCardCurrentListFragment>(
      {
        id: `List:${currentListId}`,
        fragment: MoveCardCurrentListFragmentDoc,
      },
      true,
    );

    const currentBoardData =
      client.readFragment<SuggestedMovesCurrentBoardFragment>(
        {
          id: `Board:${currentBoardId}`,
          fragment: SuggestedMovesCurrentBoardFragmentDoc,
        },
        true,
      );

    if (!currentListData || !currentBoardData) {
      return;
    }

    // Create map for performant lookups
    const currentBoardOpenLists: Map<
      string,
      SuggestedMovesCurrentBoardFragment['lists'][number]
    > = new Map();
    currentBoardData.lists.forEach((list) => {
      if (currentBoardOpenLists.has(list.id)) {
        return false;
      } else {
        currentBoardOpenLists.set(list.id, list);
      }
    });

    // Check if suggestions are valid for current user
    if (
      MemberState.get(MEMBER_STATE) === false ||
      !isLoggedIn ||
      !isMemberOfBoard(currentBoardData, memberId)
    ) {
      return;
    }

    const history = ActionHistory.get();
    const actionListIdToActionMap: Map<string, Move> = new Map();

    for (const { action, context } of history) {
      // Stop adding valid suggestions once the max number has been reached
      if (actionListIdToActionMap.size === MAX_SUGGESTIONS) {
        break;
      }

      const actionListId = (action as Move).idList;
      const actionEntry = actionListIdToActionMap.get(actionListId);

      if (action.type === 'move') {
        // If the suggested list isn't in the map, and it's a valid suggestion, then add it
        if (
          !actionEntry &&
          // Only suggest lists that have been moved to from the current list
          context.idList === currentListId &&
          // Don't suggest the current list
          action.idList !== currentListId &&
          // Restrict to open lists on the current board
          currentBoardOpenLists.has(action.idList)
        ) {
          actionListIdToActionMap.set(actionListId, action);
        }
      }
    }
    const validSuggestions = [...actionListIdToActionMap.values()];

    const suggestedLists: SuggestedList[] = [];

    validSuggestions.forEach((action) => {
      const suggestedListId = action.idList;
      const suggestedListData = currentBoardOpenLists.get(suggestedListId);

      let targetIndex = action.position;
      if (targetIndex === 'top') {
        targetIndex = 0;
      } else if (targetIndex === 'bottom') {
        const suggestedListNumCards = readListVisibleCardsFromCache({
          boardId: currentBoardId,
          listId: suggestedListId,
        }).length;
        targetIndex = suggestedListNumCards;
      }

      if (suggestedListData) {
        const direction =
          suggestedListData.pos > currentListData.pos ? 'right' : 'left';

        suggestedLists.push({
          id: suggestedListId,
          direction,
          name: suggestedListData.name,
          targetIndex,
        });
      }
    });

    return suggestedLists;
  }, [isLoggedIn, memberId, currentBoardId, currentListId]);

  return { getSuggestedMoves };
};
